import React from 'react';
import { Table } from 'antd';
import styled from 'styled-components';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { SorterResult, SortOrder } from 'antd/es/table/interface';
import { ListSortOrder } from './useListState';

type ListTableProps<TRow> = {
  rowKey: keyof TRow & string;
  columns: ColumnsType<TRow>;
  dataSource: TRow[];
  isLoading?: boolean;
  /** 행 클릭 — 보통 상세 페이지 이동. 주면 커서가 pointer 로 바뀐다 */
  onClickRow?: (row: TRow) => void;
  /** 기본값은 어드민 공용 문구(`_table/Table`)와 같다 */
  emptyText?: string;
  /** 서버 정렬. 넘기면 antd 헤더 정렬이 서버 쿼리로 이어진다 */
  sortBy?: string;
  sortOrder?: ListSortOrder;
  onChangeSort?: (sortBy?: string, sortOrder?: ListSortOrder) => void;
  /** 가로 스크롤 기준 폭 — 컬럼이 많은 리스트에서 준다 */
  scrollX?: number;
};

/**
 * 셀 여백 (AMZUWEB-684).
 *
 * 기준은 **`_table/Table`(124곳, 주문/배송관리·멀티채널 주문관리가 쓰는 것)** 이다.
 * 그쪽도 속은 antd `Table` 이라 기술은 같고, 다른 건 표현 규약뿐이라 그것만 가져온다 —
 * 테두리 · 가운데 정렬 · 회색 헤더 · 셀 여백.
 *
 * `size="small"`(8px)을 쓰면 그 화면들 옆에서 **글자가 표에 달라붙어 보인다.**
 * `size` 로 조절하지 않고 패딩을 직접 쓰는 이유 — `size` 는 글자 크기까지 같이 바꿔서
 * 여백만 조절할 수 없다.
 */
const StyledTableWrap = styled.div`
  .ant-table-thead > tr > th,
  .ant-table-tbody > tr > td {
    padding: 14px 16px;
    text-align: center;
  }

  .ant-table-thead > tr > th {
    background: #fafafa;
    white-space: nowrap;
  }

  /*
    긴 글이 들어가는 칸만 왼쪽 정렬로 둔다.
    가운데 정렬은 값이 짧을 때 읽기 좋지만, 길이가 들쭉날쭉한 제목·메모는
    줄마다 시작점이 달라져 훑기가 어려워진다.

    ⚠️ 셀렉터를 위 규칙과 같은 깊이로 써야 한다. td.is-text-left 만 쓰면
    .ant-table-tbody > tr > td (클래스1+엘리먼트2)보다 명시도가 낮아 조용히 무시된다 —
    클래스를 붙였는데 가운데 정렬 그대로인 증상이 난다.
  */
  .ant-table-thead > tr > th.is-text-left,
  .ant-table-tbody > tr > td.is-text-left {
    text-align: left;
  }
`;

/**
 * 정렬 헤더가 도는 순서. 마지막을 `'ascend'` 로 닫아 **'해제' 단계를 없앤다** —
 * 이유는 아래 `columnsWithSortState` 주석 참고.
 */
const SORT_DIRECTIONS: SortOrder[] = ['ascend', 'descend', 'ascend'];

/** antd 의 `'ascend' | 'descend'` ↔ 서버 규약(`ASC`/`DESC`) 사이 변환 */
const toServerOrder = (order?: string | null): ListSortOrder | undefined => {
  if (order === 'ascend') return 'ASC';
  if (order === 'descend') return 'DESC';
  return undefined;
};

const toAntdOrder = (order?: ListSortOrder): 'ascend' | 'descend' | undefined => {
  if (order === 'ASC') return 'ascend';
  if (order === 'DESC') return 'descend';
  return undefined;
};

/**
 * 리스트 표 (AMZUWEB-684) — antd `Table` 래퍼.
 *
 * 표를 새로 만들지 않는다. antd `Table` 은 이미 23곳이 직접 쓰고 있어서
 * 감싸기만 하면 된다. 래퍼가 맡는 것은 **화면마다 다르게 짜던 것들**이다 —
 * 페이지네이션은 `ListPager` 로 빼고(여기서는 끈다), 행 클릭·빈 상태·정렬 변환을 한 곳에 모은다.
 *
 * ⚠️ **정렬은 서버가 한다.** antd 에 `sorter: true` 만 주고 비교 함수를 주지 않는다 —
 * 함수를 주면 antd 가 **현재 페이지 안에서만** 다시 정렬해서, 표에 보이는 순서와
 * 서버가 자른 페이지 경계가 어긋난다(2페이지 첫 행이 1페이지 끝보다 앞서 보이는 식).
 */
export const ListTable = <TRow,>({
  rowKey,
  columns,
  dataSource,
  isLoading = false,
  onClickRow,
  emptyText = '데이터가 존재하지 않습니다.',
  sortBy,
  sortOrder,
  onChangeSort,
  scrollX,
}: ListTableProps<TRow>): React.JSX.Element => {
  // 헤더의 정렬 화살표를 현재 서버 정렬에 맞춰 둔다 (URL 로 들어와도 표시가 맞는다)
  const columnsWithSortState = columns.map((column) => {
    const key = (column.key ?? ('dataIndex' in column ? column.dataIndex : undefined)) as string | undefined;

    if (!column.sorter || !key) {
      return column;
    }

    return {
      ...column,
      sortOrder: key === sortBy ? toAntdOrder(sortOrder) : null,
      /**
       * 🔴 '해제' 단계를 뺀다 (기본 `['ascend','descend','ascend']`).
       *
       * antd 는 오름차순 → 내림차순 → **해제** 로 순환하는데, 해제는 `onChange` 에
       * `order: undefined` 로 와서 기본 정렬로 되돌아간다. 문제는 **기본 정렬이 걸린
       * 컬럼**이다 — 기본이 `id DESC` 면 번호 헤더의 다음 단계가 해제이고, 해제하면 다시
       * `id DESC` 라 **눌러도 아무 일이 안 일어난다.** 다른 컬럼을 거쳐야만 오름차순이 된다.
       *
       * 두 방향만 돌게 하면 어느 컬럼이 기본이든 한 번 누를 때마다 방향이 바뀐다.
       */
      sortDirections: SORT_DIRECTIONS,
    };
  });

  const handleChange = (
    _pagination: TablePaginationConfig,
    _filters: unknown,
    sorter: SorterResult<TRow> | SorterResult<TRow>[],
  ): void => {
    if (!onChangeSort) {
      return;
    }

    // 다중 정렬은 쓰지 않는다 — 서버 계약이 sortBy 하나다
    const single = Array.isArray(sorter) ? sorter[0] : sorter;
    const nextOrder = toServerOrder(single?.order);

    // 정렬 해제(3번째 클릭)면 기본 정렬로 돌린다
    onChangeSort(nextOrder ? (single?.columnKey as string) : undefined, nextOrder);
  };

  return (
    <StyledTableWrap>
      <Table<TRow>
        rowKey={rowKey}
        bordered={true}
        loading={isLoading}
        dataSource={dataSource}
        columns={columnsWithSortState}
        pagination={false}
        locale={{ emptyText }}
        scroll={scrollX ? { x: scrollX } : undefined}
        onChange={handleChange}
        onRow={
          onClickRow
            ? (row) => ({
                onClick: () => onClickRow(row),
                style: { cursor: 'pointer' },
              })
            : undefined
        }
      />
    </StyledTableWrap>
  );
};
